/* =========================================================
   Shain Pro - Collection filtering, sorting and pagination
   Uses the Section Rendering API to update results without a
   full page reload. Safe to load more than once (theme editor).
   ========================================================= */
(function () {
  'use strict';

  if (window.customElements.get('collection-facets')) return;

  var DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
  var COLUMNS_STORAGE_KEY = 'shain-pro:collection-columns';
  var CHECKBOX_DELAY = 250;
  var PRICE_DELAY = 700;
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  var responseCache = new Map();

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* Storage unavailable (private mode or blocked) - preference is simply not remembered. */
    }
  }

  function isVisible(element) {
    return !!(element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length));
  }

  function toNumber(value, fallback) {
    var number = parseFloat(value);
    return isNaN(number) ? fallback : number;
  }

  class CollectionFacets extends HTMLElement {
    constructor() {
      super();
      this.sectionId = '';
      this.layout = 'vertical';
      this.form = null;
      this.panel = null;
      this.dialog = null;
      this.timer = null;
      this.controller = null;
      this.lastFocused = null;
      this.expandedFacets = new Set();

      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleToggle = this.handleToggle.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.handleBreakpoint = this.handleBreakpoint.bind(this);
      this.updateStickyOffset = this.updateStickyOffset.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.layout = this.dataset.layout || 'vertical';
      this.refreshReferences();

      this.addEventListener('change', this.handleChange);
      this.addEventListener('input', this.handleInput);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('submit', this.handleSubmit);
      this.addEventListener('toggle', this.handleToggle, true);
      document.addEventListener('click', this.handleDocumentClick);
      window.addEventListener('popstate', this.handlePopState);
      window.addEventListener('resize', this.updateStickyOffset, { passive: true });

      if (DESKTOP_QUERY.addEventListener) {
        DESKTOP_QUERY.addEventListener('change', this.handleBreakpoint);
      } else if (DESKTOP_QUERY.addListener) {
        DESKTOP_QUERY.addListener(this.handleBreakpoint);
      }

      this.applyStoredColumns();
      this.syncAllPriceSliders();
      this.updateStickyOffset();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('input', this.handleInput);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('submit', this.handleSubmit);
      this.removeEventListener('toggle', this.handleToggle, true);
      document.removeEventListener('click', this.handleDocumentClick);
      window.removeEventListener('popstate', this.handlePopState);
      window.removeEventListener('resize', this.updateStickyOffset);

      if (DESKTOP_QUERY.removeEventListener) {
        DESKTOP_QUERY.removeEventListener('change', this.handleBreakpoint);
      } else if (DESKTOP_QUERY.removeListener) {
        DESKTOP_QUERY.removeListener(this.handleBreakpoint);
      }

      window.clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      document.body.classList.remove('facets-drawer-open');
    }

    refreshReferences() {
      this.form = document.getElementById('FacetsForm-' + this.sectionId);
      this.panel = this.querySelector('[data-facets-panel]');
      this.dialog = this.panel ? this.panel.querySelector('[data-facets-dialog]') : null;
    }

    /* ---------------- State helpers ---------------- */
    isDrawerMode() {
      return this.layout === 'drawer' || !DESKTOP_QUERY.matches;
    }

    isDrawerOpen() {
      return !!(this.panel && this.panel.classList.contains('is-open'));
    }

    buildSearchParams() {
      var params = new URLSearchParams();
      if (!this.form) return params;

      var data = new FormData(this.form);
      data.forEach(function (value, key) {
        if (typeof value === 'string' && value.trim() !== '') {
          params.append(key, value.trim());
        }
      });

      return params;
    }

    /* ---------------- Events ---------------- */
    handleChange(event) {
      var target = event.target;
      if (!this.form || target.form !== this.form) return;

      if (target.matches('[data-price-min], [data-price-max]')) {
        this.normalisePriceInputs(target.closest('[data-facet-price]'));
        this.queueRender(0);
        return;
      }

      if (target.matches('[data-facets-sort]')) {
        this.queueRender(0);
        return;
      }

      if (target.type === 'checkbox') {
        this.queueRender(CHECKBOX_DELAY);
      }
    }

    handleInput(event) {
      var target = event.target;
      var wrapper = target.closest('[data-facet-price]');
      if (!wrapper) return;

      if (target.matches('[data-range-min], [data-range-max-input]')) {
        this.onRangeInput(wrapper, target);
        this.queueRender(PRICE_DELAY);
        return;
      }

      if (target.matches('[data-price-min], [data-price-max]')) {
        this.syncPriceSlider(wrapper);
        this.queueRender(PRICE_DELAY);
      }
    }

    handleSubmit(event) {
      if (event.target !== this.form) return;
      event.preventDefault();
      this.queueRender(0);
    }

    handleClick(event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      var opener = target.closest('[data-facets-open]');
      if (opener) {
        event.preventDefault();
        this.openDrawer(opener);
        return;
      }

      var closer = target.closest('[data-facets-close]');
      if (closer) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      var more = target.closest('[data-facet-more]');
      if (more) {
        event.preventDefault();
        this.toggleMore(more);
        return;
      }

      var columnsButton = target.closest('[data-grid-columns]');
      if (columnsButton) {
        event.preventDefault();
        this.setColumns(columnsButton.getAttribute('data-grid-columns'), true);
        return;
      }

      var loadMore = target.closest('[data-facets-load-more]');
      if (loadMore) {
        event.preventDefault();
        this.loadMore(loadMore);
        return;
      }

      var link = target.closest('[data-facets-link], [data-facets-pagination] a[href]');
      if (link && this.isSameOriginLink(link)) {
        event.preventDefault();
        var url = new URL(link.href, window.location.origin);
        var isPagination = !!link.closest('[data-facets-pagination]');
        this.render(url.searchParams.toString(), { push: true, scroll: true, keepDrawer: !isPagination });
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (this.isDrawerOpen()) {
          event.preventDefault();
          this.closeDrawer();
          return;
        }

        var openFacet = this.getOpenHorizontalFacet();
        if (openFacet) {
          event.preventDefault();
          openFacet.open = false;
          var summary = openFacet.querySelector('summary');
          if (summary) summary.focus();
        }
        return;
      }

      if (event.key === 'Tab' && this.isDrawerOpen()) {
        this.trapFocus(event);
      }
    }

    handleToggle(event) {
      var facet = event.target;
      if (!(facet instanceof HTMLDetailsElement) || !facet.matches('[data-facet]')) return;
      if (!this.isHorizontalDropdownMode() || !facet.open) return;

      this.querySelectorAll('[data-facet][open]').forEach(function (other) {
        if (other !== facet) other.open = false;
      });
    }

    handleDocumentClick(event) {
      if (!this.isHorizontalDropdownMode()) return;
      var openFacet = this.getOpenHorizontalFacet();
      if (openFacet && !openFacet.contains(event.target)) {
        openFacet.open = false;
      }
    }

    handlePopState() {
      if (!this.isConnected) return;
      this.render(window.location.search.replace(/^\?/, ''), { push: false, scroll: false, keepDrawer: true });
    }

    handleBreakpoint() {
      if (!this.isDrawerMode() && this.isDrawerOpen()) {
        this.closeDrawer(true);
      }

      if (!this.isHorizontalDropdownMode()) return;
      this.querySelectorAll('[data-facet][open]').forEach(function (facet) {
        facet.open = false;
      });
    }

    isHorizontalDropdownMode() {
      return this.layout === 'horizontal' && DESKTOP_QUERY.matches;
    }

    getOpenHorizontalFacet() {
      if (!this.isHorizontalDropdownMode()) return null;
      return this.querySelector('[data-facet][open]');
    }

    isSameOriginLink(link) {
      try {
        return new URL(link.href, window.location.origin).origin === window.location.origin;
      } catch (error) {
        return false;
      }
    }

    /* ---------------- Rendering ---------------- */
    queueRender(delay) {
      var self = this;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(function () {
        var searchString = self.buildSearchParams().toString();
        if (self.buildPageUrl(searchString) === self.buildPageUrl(window.location.search.replace(/^\?/, ''))) return;
        self.render(searchString, { push: true, scroll: false, keepDrawer: true });
      }, delay);
    }

    buildSectionUrl(searchString) {
      var params = new URLSearchParams(searchString);
      params.delete('section_id');
      params.set('section_id', this.sectionId);
      return window.location.pathname + '?' + params.toString();
    }

    buildPageUrl(searchString) {
      var params = new URLSearchParams(searchString);
      params.delete('section_id');
      var query = params.toString();
      return window.location.pathname + (query ? '?' + query : '');
    }

    fetchSection(url, signal) {
      if (responseCache.has(url)) {
        return Promise.resolve(responseCache.get(url));
      }

      return fetch(url, { signal: signal, credentials: 'same-origin' }).then(function (response) {
        if (!response.ok) throw new Error('Request failed with status ' + response.status);
        return response.text().then(function (html) {
          responseCache.set(url, html);
          return html;
        });
      });
    }

    render(searchString, options) {
      var self = this;
      var opts = options || {};
      var pageUrl = this.buildPageUrl(searchString);
      var sectionUrl = this.buildSectionUrl(searchString);

      window.clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      this.controller = controller;

      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');

      return this.fetchSection(sectionUrl, controller ? controller.signal : undefined)
        .then(function (html) {
          if (controller && controller.signal.aborted) return;

          self.applyResponse(html);

          if (opts.push && pageUrl !== window.location.pathname + window.location.search) {
            window.history.pushState({ collectionFacets: self.sectionId }, '', pageUrl);
          }

          if (!opts.keepDrawer && self.isDrawerOpen()) {
            self.closeDrawer(true);
          }

          if (opts.scroll) self.scrollToResults();
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          window.location.assign(pageUrl);
        })
        .finally(function () {
          if (self.controller !== controller) return;
          self.controller = null;
          self.classList.remove('is-loading');
          self.removeAttribute('aria-busy');
        });
    }

    applyResponse(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var self = this;

      this.querySelectorAll('[data-facets-sync]').forEach(function (element) {
        var key = element.getAttribute('data-facets-sync');
        var source = doc.querySelector('[data-facets-sync="' + key + '"]');
        if (!source) return;

        if (key === 'list') {
          self.mergeFacetList(element, source);
        } else {
          element.innerHTML = source.innerHTML;
        }
      });

      var newSort = doc.querySelector('[data-facets-sort]');
      var currentSort = this.querySelector('[data-facets-sort]');
      if (newSort && currentSort && currentSort !== document.activeElement) {
        currentSort.value = newSort.value;
      }

      this.restoreExpanded();
      this.applyStoredColumns();
      this.syncAllPriceSliders();
      this.announceResults(doc);
      this.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true }));
    }

    mergeFacetList(current, source) {
      var active = document.activeElement;
      var activeId = active && current.contains(active) ? active.id : '';
      var keepFacet = null;

      if (active && current.contains(active) && active.matches('input[type="number"], input[type="range"]')) {
        keepFacet = active.closest('[data-facet]');
      }

      var openState = {};
      current.querySelectorAll('[data-facet]').forEach(function (facet) {
        openState[facet.id] = facet.open;
      });

      var fragment = document.createDocumentFragment();
      Array.prototype.forEach.call(source.querySelectorAll(':scope > [data-facet]'), function (incoming) {
        if (keepFacet && keepFacet.id === incoming.id) {
          fragment.appendChild(keepFacet);
          return;
        }

        var node = document.importNode(incoming, true);
        if (Object.prototype.hasOwnProperty.call(openState, node.id)) {
          node.open = openState[node.id];
        }
        fragment.appendChild(node);
      });

      current.replaceChildren(fragment);

      if (activeId && (!keepFacet || !keepFacet.contains(document.activeElement))) {
        var restored = document.getElementById(activeId);
        if (restored && isVisible(restored)) {
          restored.focus({ preventScroll: true });
        }
      }
    }

    announceResults(doc) {
      var live = this.querySelector('[data-facets-live]');
      var count = doc.querySelector('[data-facets-sync="count"]') || doc.querySelector('.facets__apply');
      if (live && count) {
        live.textContent = '';
        window.setTimeout(function () {
          live.textContent = count.textContent.trim();
        }, 100);
      }
    }

    scrollToResults() {
      var results = this.querySelector('.collection-results');
      if (!results || this.isDrawerOpen()) return;

      var offset = toNumber(getComputedStyle(this).getPropertyValue('--facets-sticky-top'), 24);
      var top = results.getBoundingClientRect().top + window.pageYOffset - offset - 12;
      if (top < window.pageYOffset) {
        var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    /* ---------------- Load more ---------------- */
    loadMore(button) {
      var self = this;
      if (button.classList.contains('is-loading')) return;

      var url = new URL(button.href, window.location.origin);
      var sectionUrl = this.buildSectionUrl(url.searchParams.toString());
      var grid = this.querySelector('[data-facets-grid]');
      if (!grid) return;

      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      this.fetchSection(sectionUrl)
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = doc.querySelectorAll('[data-facets-grid] > li');
          var firstNew = null;

          Array.prototype.forEach.call(items, function (item, index) {
            var node = document.importNode(item, true);
            if (index === 0) firstNew = node;
            grid.appendChild(node);
          });

          var wrap = self.querySelector('[data-facets-load-more-wrap]');
          var newWrap = doc.querySelector('[data-facets-load-more-wrap]');
          if (wrap && newWrap) {
            wrap.innerHTML = newWrap.innerHTML;
          } else if (wrap) {
            wrap.remove();
          }

          self.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true }));

          if (firstNew) {
            var focusTarget = firstNew.querySelector('a[href]:not([tabindex="-1"])');
            if (focusTarget) focusTarget.focus({ preventScroll: true });
          }
        })
        .catch(function () {
          window.location.assign(url.pathname + url.search);
        })
        .finally(function () {
          if (button.isConnected) {
            button.classList.remove('is-loading');
            button.removeAttribute('aria-busy');
          }
        });
    }

    /* ---------------- Drawer ---------------- */
    openDrawer(trigger) {
      if (!this.panel || !this.dialog) return;

      this.lastFocused = trigger || document.activeElement;
      this.panel.classList.add('is-open');
      this.dialog.setAttribute('role', 'dialog');
      this.dialog.setAttribute('aria-modal', 'true');
      document.body.classList.add('facets-drawer-open');
      this.setTriggersExpanded(true);

      var dialog = this.dialog;
      window.requestAnimationFrame(function () {
        dialog.focus({ preventScroll: true });
      });
    }

    closeDrawer(skipFocus) {
      if (!this.panel || !this.isDrawerOpen()) return;

      this.panel.classList.remove('is-open');
      if (this.dialog) {
        this.dialog.removeAttribute('role');
        this.dialog.removeAttribute('aria-modal');
      }
      document.body.classList.remove('facets-drawer-open');
      this.setTriggersExpanded(false);

      if (!skipFocus && this.lastFocused && typeof this.lastFocused.focus === 'function' && isVisible(this.lastFocused)) {
        this.lastFocused.focus({ preventScroll: true });
      }
      this.lastFocused = null;
    }

    setTriggersExpanded(expanded) {
      this.querySelectorAll('[data-facets-open]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    trapFocus(event) {
      if (!this.dialog) return;
      var focusable = Array.prototype.filter.call(this.dialog.querySelectorAll(FOCUSABLE), isVisible);
      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------------- Show more ---------------- */
    toggleMore(button) {
      var facet = button.closest('[data-facet]');
      if (!facet) return;

      var expanded = !facet.classList.contains('is-expanded');
      facet.classList.toggle('is-expanded', expanded);
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      button.textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;

      if (expanded) {
        this.expandedFacets.add(facet.id);
        var firstExtra = facet.querySelector('[data-facet-extra] input:not([disabled])');
        if (firstExtra) firstExtra.focus({ preventScroll: true });
      } else {
        this.expandedFacets.delete(facet.id);
      }
    }

    restoreExpanded() {
      var self = this;
      this.expandedFacets.forEach(function (id) {
        var facet = document.getElementById(id);
        if (!facet || !self.contains(facet)) {
          self.expandedFacets.delete(id);
          return;
        }

        facet.classList.add('is-expanded');
        var button = facet.querySelector('[data-facet-more]');
        if (button) {
          button.setAttribute('aria-expanded', 'true');
          button.textContent = button.dataset.labelLess;
        }
      });
    }

    /* ---------------- Grid columns ---------------- */
    setColumns(value, persist) {
      var columns = parseInt(value, 10);
      if (!columns || columns < 2 || columns > 5) return;

      var grid = this.querySelector('[data-facets-grid]');
      if (grid) grid.style.setProperty('--columns-desktop', columns);

      this.querySelectorAll('[data-grid-columns]').forEach(function (button) {
        var pressed = parseInt(button.getAttribute('data-grid-columns'), 10) === columns;
        button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      });

      if (persist) storageSet(COLUMNS_STORAGE_KEY, String(columns));
    }

    applyStoredColumns() {
      if (this.dataset.enableSwitcher !== 'true') return;
      var stored = storageGet(COLUMNS_STORAGE_KEY);
      this.setColumns(stored || this.dataset.columnsDefault, false);
    }

    /* ---------------- Price range ---------------- */
    syncAllPriceSliders() {
      var self = this;
      this.querySelectorAll('[data-facet-price]').forEach(function (wrapper) {
        self.syncPriceSlider(wrapper);
      });
    }

    getPriceParts(wrapper) {
      return {
        max: toNumber(wrapper.dataset.rangeMax, 0),
        minInput: wrapper.querySelector('[data-price-min]'),
        maxInput: wrapper.querySelector('[data-price-max]'),
        minRange: wrapper.querySelector('[data-range-min]'),
        maxRange: wrapper.querySelector('[data-range-max-input]')
      };
    }

    onRangeInput(wrapper, range) {
      var parts = this.getPriceParts(wrapper);
      if (!parts.minRange || !parts.maxRange) return;

      var minValue = toNumber(parts.minRange.value, 0);
      var maxValue = toNumber(parts.maxRange.value, parts.max);

      if (minValue > maxValue) {
        if (range === parts.minRange) {
          minValue = maxValue;
          parts.minRange.value = minValue;
        } else {
          maxValue = minValue;
          parts.maxRange.value = maxValue;
        }
      }

      wrapper.classList.toggle('is-min-on-top', range === parts.minRange || minValue >= parts.max);

      if (parts.minInput) parts.minInput.value = minValue > 0 ? minValue : '';
      if (parts.maxInput) parts.maxInput.value = maxValue < parts.max ? maxValue : '';

      this.paintPriceTrack(wrapper, minValue, maxValue, parts.max);
    }

    syncPriceSlider(wrapper) {
      if (!wrapper) return;
      var parts = this.getPriceParts(wrapper);
      if (!parts.minRange || !parts.maxRange) return;

      var minValue = Math.max(0, Math.min(toNumber(parts.minInput && parts.minInput.value, 0), parts.max));
      var maxValue = Math.max(0, Math.min(toNumber(parts.maxInput && parts.maxInput.value, parts.max), parts.max));
      if (minValue > maxValue) minValue = maxValue;

      parts.minRange.value = minValue;
      parts.maxRange.value = maxValue;
      wrapper.classList.toggle('is-min-on-top', minValue >= parts.max);

      this.paintPriceTrack(wrapper, minValue, maxValue, parts.max);
    }

    normalisePriceInputs(wrapper) {
      if (!wrapper) return;
      var parts = this.getPriceParts(wrapper);
      if (!parts.minInput || !parts.maxInput) return;

      var hasMin = parts.minInput.value.trim() !== '';
      var hasMax = parts.maxInput.value.trim() !== '';
      var minValue = hasMin ? Math.max(0, Math.min(Math.floor(toNumber(parts.minInput.value, 0)), parts.max)) : null;
      var maxValue = hasMax ? Math.max(0, Math.min(Math.ceil(toNumber(parts.maxInput.value, parts.max)), parts.max)) : null;

      if (minValue !== null && maxValue !== null && minValue > maxValue) {
        var swap = minValue;
        minValue = maxValue;
        maxValue = swap;
      }

      parts.minInput.value = minValue === null || minValue === 0 ? '' : minValue;
      parts.maxInput.value = maxValue === null || maxValue === parts.max ? '' : maxValue;

      this.syncPriceSlider(wrapper);
    }

    paintPriceTrack(wrapper, minValue, maxValue, max) {
      var safeMax = max > 0 ? max : 1;
      wrapper.style.setProperty('--range-start', (minValue / safeMax) * 100 + '%');
      wrapper.style.setProperty('--range-end', (maxValue / safeMax) * 100 + '%');
    }

    /* ---------------- Sticky sidebar offset ---------------- */
    updateStickyOffset() {
      var header = document.querySelector('.header-wrapper--sticky');
      var offset = 24;

      if (header) {
        var position = getComputedStyle(header).position;
        var parentPosition = header.parentElement ? getComputedStyle(header.parentElement).position : '';
        if (position === 'sticky' || position === 'fixed' || parentPosition === 'sticky' || parentPosition === 'fixed') {
          offset += Math.round(header.getBoundingClientRect().height);
        }
      }

      this.style.setProperty('--facets-sticky-top', offset + 'px');
    }
  }

  if (!window.customElements.get('collection-facets')) {
    window.customElements.define('collection-facets', CollectionFacets);
  }
})();
